/**
 *   OPEN ──(tick reaches target)──► TARGET_ACHIEVED
 *     ├──(tick reaches stoploss)──► STOPLOSS_HIT
 *     ├──(its contract settles)───► EXPIRED
 *     └──(closed by hand)──────────► TARGET_ACHIEVED | STOPLOSS_HIT | MANUAL_CLOSE
 */
import { z } from 'zod';
import type { TradeType } from '../primitives.ts';
import { type Frame, standingR } from '../r.ts';
import { todayInIst } from '../when.ts';
import { type Labelled, recommendationLabel } from './recommendation-ref.ts';

/** Excludes EXPIRED: only its contract settling expires a recommendation. */
export const CLOSABLE_RECOMMENDATION_STATUSES = [
  'TARGET_ACHIEVED',
  'STOPLOSS_HIT',
  'MANUAL_CLOSE',
] as const;
export type ClosableRecommendationStatus = (typeof CLOSABLE_RECOMMENDATION_STATUSES)[number];

export const CLOSED_RECOMMENDATION_STATUSES = [
  ...CLOSABLE_RECOMMENDATION_STATUSES,
  'EXPIRED',
] as const;
export type ClosedRecommendationStatus = (typeof CLOSED_RECOMMENDATION_STATUSES)[number];
export const RECOMMENDATION_STATUSES = ['OPEN', ...CLOSED_RECOMMENDATION_STATUSES] as const;
export type RecommendationStatus = (typeof RECOMMENDATION_STATUSES)[number];

export const RECOMMENDATION_FILTERS = ['open', 'closed', 'all'] as const;
export type RecommendationFilter = (typeof RECOMMENDATION_FILTERS)[number];

export const RECOMMENDATION_STATUS_LABEL: Record<RecommendationStatus, string> = {
  OPEN: 'Open',
  TARGET_ACHIEVED: 'Target achieved',
  STOPLOSS_HIT: 'Stoploss hit',
  MANUAL_CLOSE: 'Closed by the analyst',
  EXPIRED: 'Expired',
};

export function closingPriceFor(
  levels: Pick<RecommendationLevels, 'targetPrice' | 'stoplossPrice'>,
  status: ClosedRecommendationStatus,
): number | null {
  if (status === 'TARGET_ACHIEVED') return levels.targetPrice;
  if (status === 'STOPLOSS_HIT') return levels.stoplossPrice;
  return null;
}

/**
 * The filed R, a unit: entry to the stop the recommendation was filed with. Revising the stop
 * moves where it closes, never what it risked, so a trailed stop neither inflates nor voids it.
 */
export const filedR = (
  recommendation: Pick<RecommendationSnapshot, 'tradeType' | 'entryPrice' | 'filedStoplossPrice'>,
): number | null =>
  standingR({ ...recommendation, stoplossPrice: recommendation.filedStoplossPrice });

export const stopMoved = (
  recommendation: Pick<RecommendationSnapshot, 'stoplossPrice' | 'filedStoplossPrice'>,
): boolean => recommendation.stoplossPrice !== recommendation.filedStoplossPrice;

export function frameOf(
  recommendation: Pick<
    RecommendationSnapshot,
    'tradeType' | 'entryPrice' | 'targetPrice' | 'stoplossPrice' | 'filedStoplossPrice'
  >,
): Frame {
  return {
    tradeType: recommendation.tradeType,
    entryPrice: recommendation.entryPrice,
    targetPrice: recommendation.targetPrice,
    stoplossPrice: recommendation.stoplossPrice,
    riskPerUnit: filedR(recommendation),
  };
}

export interface RecommendationLevels {
  tradeType: TradeType;
  entryPrice: number;
  targetPrice: number;
  stoplossPrice: number;
}

export interface RecommendationSnapshot extends RecommendationLevels {
  filedStoplossPrice: number;
  status: RecommendationStatus;
  closedPrice: number | null;
  /**
   * An open recommendation's market now, the live quote or the last close, where a reader has
   * one. Never the quote it was filed at; unset, it is unpriced.
   */
  mark?: number | null;
}

export function settledWithItsContract(status: RecommendationStatus): boolean {
  return status === 'EXPIRED';
}

export function isClosed(status: RecommendationStatus): boolean {
  return status !== 'OPEN';
}

/** Said of an id off the wire: the id is internal, and a reference may not be the reader's to see. */
export const NO_SUCH_RECOMMENDATION = 'no such recommendation';

export function recommendationAlreadyClosed(
  recommendation: Labelled & { status: RecommendationStatus },
): string {
  return `${recommendationLabel(recommendation)} is already closed (${RECOMMENDATION_STATUS_LABEL[recommendation.status]})`;
}

export const NOTHING_TO_REVISE = 'nothing to revise — move the target, the stoploss, or both';

export const NOTHING_TO_EDIT =
  'nothing to correct — give an entry, a target, a stoploss, a reference or a date';

export function recommendationRefileHeldMessage(
  recommendation: Labelled,
  positions: number,
): string {
  const held = positions === 1 ? 'one position stands' : `${positions} positions stand`;
  return (
    `${recommendationLabel(recommendation)} cannot be refiled — ${held} against it, and the book a member ` +
    'took a recommendation under is theirs, not the analyst’s to move afterwards'
  );
}

export function recommendationHeldMessage(recommendation: Labelled, positions: number): string {
  const held = positions === 1 ? 'one position stands' : `${positions} positions stand`;
  return (
    `${recommendationLabel(recommendation)} cannot be removed — ${held} against it, and a member's own ` +
    'record names the recommendation it was taken against. Close it instead'
  );
}

export const recommendedOnSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'recommendation date must be an ISO day (yyyy-mm-dd)');

export function validateRecommendedOn(recommendedOn: string, now: Date): string[] {
  const today = todayInIst(now);
  return recommendedOn > today
    ? [`recommendation date ${recommendedOn} is in the future (today is ${today})`]
    : [];
}

export function markedToMarket<T extends RecommendationSnapshot>(
  recommendation: T,
  mark: number | null,
): T {
  return isClosed(recommendation.status) ? recommendation : { ...recommendation, mark };
}
